import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, Circle, WarningCircle, Fire, TrendUp, NotePencil, Users, Target, SmileyMeh, Smiley, SmileySad, Sparkle, Trophy, CalendarBlank } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Md } from '@/components/Md';
import ChallengeContentOfDay from '@/components/ChallengeContentOfDay';
import { fmtDate as fmtDatePt } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;
const CAT_COLORS = { 'alimentação': 'bg-brand-mint/15 text-brand-green', 'hidratação': 'bg-blue-50 text-blue-500', 'comportamento': 'bg-purple-50 text-purple-500', 'estilo de vida': 'bg-amber-50 text-amber-600', 'infantil': 'bg-pink-50 text-pink-500' };
const STATUS_ICONS = { done: CheckCircle, partial: WarningCircle, not_done: Circle };
const STATUS_COLORS = { done: 'text-brand-green', partial: 'text-brand-gold', not_done: 'text-brand-placeholder' };
const STATUS_BG = { done: 'bg-brand-green/10 border-brand-green/20', partial: 'bg-brand-gold/10 border-brand-gold/20', not_done: 'bg-brand-bg border-brand-border' };
const FEELINGS = [
  { key: 'fácil', icon: Smiley, color: 'text-brand-green', bg: 'bg-brand-green/10 border-brand-green/20' },
  { key: 'neutro', icon: SmileyMeh, color: 'text-brand-gold', bg: 'bg-brand-gold/10 border-brand-gold/20' },
  { key: 'difícil', icon: SmileySad, color: 'text-red-400', bg: 'bg-red-50 border-red-200' },
];

function FeedbackMessage({ pct }) {
  if (pct >= 70) return <p className="text-xs text-brand-green font-medium mt-2">Boa consistência hoje. Continua assim!</p>;
  if (pct >= 40) return <p className="text-xs text-brand-gold-hover font-medium mt-2">Dia intermédio. Continua, cada passo conta.</p>;
  if (pct > 0) return <p className="text-xs text-brand-muted font-medium mt-2">Hoje foi mais difícil. Amanhã recomeças.</p>;
  return null;
}

export default function MetasPage() {
  const [goals, setGoals] = useState([]);
  const [logs, setLogs] = useState({});
  const [summary, setSummary] = useState(null);
  const [challenge, setChallenge] = useState(null);
  const [challengeStats, setChallengeStats] = useState(null);
  const [showNote, setShowNote] = useState(false);
  const [dailyNote, setDailyNote] = useState('');
  const [showFeeling, setShowFeeling] = useState(false);
  const [todayFeeling, setTodayFeeling] = useState(null);
  const [feelingsHistory, setFeelingsHistory] = useState([]); // PROMPT 5, Item 3a
  const [showAllFeelings, setShowAllFeelings] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [noteDirty, setNoteDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newDayBanner, setNewDayBanner] = useState(false);
  // TASK 1: dias em atraso por meta — { goal_id: [ "YYYY-MM-DD", ... ] }
  const [pendingDays, setPendingDays] = useState({});
  const [showOverdue, setShowOverdue] = useState(true);
  const [publicChallenges, setPublicChallenges] = useState([]);
  const [joiningId, setJoiningId] = useState(null);
  const today = new Date().toISOString().split('T')[0];

  // P1 — banner "novo dia": mostra quando o dia avançou face ao último visto
  useEffect(() => {
    if (!challenge?.id || !challengeStats?.day_num) return;
    const key = `challenge_day_seen_${challenge.id}`;
    const lastSeen = parseInt(localStorage.getItem(key) || '0', 10);
    if (challengeStats.day_num > lastSeen) setNewDayBanner(true);
  }, [challenge?.id, challengeStats?.day_num]);
  const dismissNewDayBanner = () => {
    if (!challenge?.id) return;
    localStorage.setItem(`challenge_day_seen_${challenge.id}`, String(challengeStats?.day_num || 1));
    setNewDayBanner(false);
  };

  const fetchAll = useCallback(async () => {
    try {
      const [goalsRes, logsRes, summaryRes, challengeRes, pendingRes] = await Promise.all([
        axios.get(`${API}/api/my-behavioral-goals`),
        axios.get(`${API}/api/goal-logs?date=${today}`),
        axios.get(`${API}/api/goal-summary?date=${today}`),
        axios.get(`${API}/api/my-challenge`),
        axios.get(`${API}/api/my-behavioral-goals/pending-days`),
      ]);
      setGoals(goalsRes.data);
      const lm = {}; logsRes.data.forEach(l => { lm[l.goal_id] = l; }); setLogs(lm);
      setSummary(summaryRes.data);
      setChallenge(challengeRes.data);
      // TASK 1: dias em atraso
      setPendingDays(pendingRes.data?.pending || {});
      try { const { data: pub } = await axios.get(`${API}/api/challenges/browse`); setPublicChallenges(Array.isArray(pub) ? pub : []); } catch {}
      if (challengeRes.data?.id) {
        try { const { data } = await axios.get(`${API}/api/challenges/${challengeRes.data.id}/stats`); setChallengeStats(data); } catch {}
        try {
          const { data: feelings } = await axios.get(`${API}/api/my-challenge-feelings?challenge_id=${challengeRes.data.id}`);
          const todayDoc = (feelings || []).find(f => f.date === today);
          setTodayFeeling(todayDoc || null);
          // PROMPT 5 — Item 3a: thread completa (mais recente primeiro), excluindo o dia de hoje.
          // Mostra apenas dias com nota da paciente OU nota da nutri (omite dias só com mood/feeling).
          setFeelingsHistory((feelings || []).filter(f => f.date !== today && (f.note || f.nutri_note)));
        } catch {}
      }
    } catch {} finally { setLoading(false); }
  }, [today]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Sync note draft from server doc only when user hasn't started editing.
  useEffect(() => {
    if (!noteDirty && todayFeeling?.note != null && todayFeeling.note !== noteDraft) {
      setNoteDraft(todayFeeling.note);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todayFeeling?.note]);

  const logGoal = async (goalId, status, value) => {
    try {
      const { data } = await axios.post(`${API}/api/goal-logs`, { goal_id: goalId, date: today, status, value });
      setLogs(prev => ({ ...prev, [goalId]: data }));
      const { data: s } = await axios.get(`${API}/api/goal-summary?date=${today}`);
      setSummary(s);
      // Show feeling prompt after logging all goals
      const newDone = Object.values({ ...logs, [goalId]: data }).filter(l => l.status === 'done' || l.status === 'partial').length;
      if (newDone >= goals.length && goals.length > 0) setShowFeeling(true);
    } catch { toast.error('Erro ao guardar'); }
  };

  // TASK 1: regista uma meta para um dia em atraso (retroactive). Sem auto-completar nada.
  const logOverdueDay = async (goalId, dateStr, status = 'done') => {
    try {
      await axios.post(`${API}/api/goal-logs`, { goal_id: goalId, date: dateStr, status });
      // Remove o dia da lista de pendentes
      setPendingDays(prev => ({ ...prev, [goalId]: (prev[goalId] || []).filter(d => d !== dateStr) }));
      toast.success(`Marcado para ${dateStr}`);
    } catch { toast.error('Erro ao guardar'); }
  };

  const handleQuantitative = (goal, value) => {
    const target = parseFloat(goal.target_value) || 1;
    const actual = parseFloat(value) || 0;
    const pct = (actual / target) * 100;
    logGoal(goal.id, pct >= 100 ? 'done' : pct >= 50 ? 'partial' : 'not_done', actual);
  };

  const logFeeling = async (feeling) => {
    await axios.post(`${API}/api/challenge-feeling`, { challenge_id: challenge?.id, feeling });
    setShowFeeling(false);
    toast.success('Obrigada pelo teu feedback');
  };

  const saveNote = async () => {
    if (!challenge?.id) return;
    try {
      await axios.post(`${API}/api/challenge-feeling`, { challenge_id: challenge.id, note: noteDraft });
      setNoteDirty(false);
      setTodayFeeling(prev => ({ ...(prev || { date: today }), note: noteDraft }));
      toast.success('Nota guardada');
    } catch { toast.error('Erro a guardar'); }
  };

  const joinChallenge = async (c) => {
    setJoiningId(c.id);
    try {
      const { data } = await axios.post(`${API}/api/challenges/${c.id}/join`);
      toast.success(data?.joined_day > 1 ? `Inscrita! Entraste no dia ${data.joined_day} de ${data.total_days}.` : 'Inscrita no desafio!');
      await fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao inscrever'); }
    finally { setJoiningId(null); }
  };

  const streak = summary?.streak || 0;
  const dayPct = summary?.pct || 0;
  const done = summary?.done || 0;
  const total = summary?.total || 0;
  const weekly = summary?.weekly || {};
  const hasChallenge = !!challenge;
  const dayNum = challengeStats?.day_num || 0;
  const totalDays = challengeStats?.total_days || 0;
  const groupPct = challengeStats?.group_pct || 0;

  // Separate challenge goals from individual goals
  const challengeGoals = hasChallenge ? goals.filter(g => g.challenge_id === challenge.id) : [];
  const individualGoals = goals.filter(g => !g.challenge_id);
  const displayGoals = hasChallenge ? challengeGoals : goals;

  if (loading) return <div className="text-center py-12 text-brand-muted">A carregar...</div>;

  return (
    <div className="fade-in" data-testid="metas-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0 flex-1">
          {hasChallenge ? (
            <>
              <h2 className="font-heading font-medium text-lg text-brand-text break-words" data-testid="challenge-title">{challenge.title}</h2>
              <p className="text-xs text-brand-muted mt-0.5">Dia {dayNum} de {totalDays}</p>
            </>
          ) : (
            <>
              <h2 className="font-heading font-medium text-xl text-brand-text">Hoje</h2>
              <p className="text-sm text-brand-muted mt-0.5">{new Date().toLocaleDateString('pt-PT', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
            </>
          )}
        </div>
        {streak > 0 && (
          <div className="flex items-center gap-1.5 bg-brand-gold/10 px-3 py-1.5 rounded-full flex-shrink-0" data-testid="streak-badge">
            <Fire size={16} weight="fill" className="text-brand-gold" />
            <span className="text-sm font-semibold text-brand-gold-hover">{streak}</span>
          </div>
        )}
      </div>

      {/* Challenge day progress bar */}
      {hasChallenge && totalDays > 0 && (
        <div className="w-full h-1.5 bg-brand-border rounded-full overflow-hidden mb-4">
          <div className="h-full bg-brand-green rounded-full transition-all" style={{ width: `${Math.min(100, (dayNum / totalDays) * 100)}%` }} />
        </div>
      )}

      {/* P1 — Banner "novo dia disponível" (mostra quando o user ainda não viu o conteúdo de hoje, sinalizado por challenge_day_seen em localStorage) */}
      {hasChallenge && dayNum > 0 && newDayBanner && (
        <div className="bg-brand-mint/15 border border-brand-green/30 rounded-2xl p-3 mb-4 flex items-center justify-between" data-testid="new-day-banner">
          <div className="flex items-center gap-2">
            <Sparkle size={18} weight="fill" className="text-brand-green" />
            <div>
              <p className="text-xs font-medium text-brand-green">Dia {dayNum} disponível</p>
              <p className="text-[10px] text-brand-muted mt-0.5">Há conteúdo novo para ti.</p>
            </div>
          </div>
          <button onClick={dismissNewDayBanner} className="text-[10px] text-brand-green hover:underline" data-testid="dismiss-new-day">Compreendido</button>
        </div>
      )}

      {/* P1 — Conteúdo de hoje (ficheiros) */}
      {hasChallenge && <ChallengeContentOfDay challengeId={challenge.id} dayNum={dayNum} />}

      {/* PROMPT 5 — Item 3a: Histórico em thread (paciente + nutri) — mais recente em cima */}
      {hasChallenge && feelingsHistory.length > 0 && (
        <div className="bg-white border border-brand-border rounded-2xl p-3 mb-4" data-testid="feelings-history">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] text-brand-muted font-medium uppercase tracking-wide">Histórico de notas do desafio</p>
            {feelingsHistory.length > 3 && (
              <button type="button" onClick={() => setShowAllFeelings(v => !v)} className="text-[10px] text-brand-green hover:underline" data-testid="feelings-history-toggle">{showAllFeelings ? 'Mostrar menos' : `Ver todos (${feelingsHistory.length})`}</button>
            )}
          </div>
          <div className="space-y-2">
            {(showAllFeelings ? feelingsHistory : feelingsHistory.slice(0, 3)).map(f => (
              <div key={f.date} className="border border-brand-border/40 rounded-xl p-2.5" data-testid={`feeling-day-${f.date}`}>
                <p className="text-[10px] text-brand-muted mb-1">{fmtDatePt(f.date)}</p>
                {f.note && (
                  <div className="mb-1.5">
                    <span className="text-[9px] text-brand-muted font-semibold uppercase tracking-wide">Eu escrevi:</span>
                    <p className="text-xs text-brand-text italic mt-0.5">&quot;{f.note}&quot;</p>
                  </div>
                )}
                {f.nutri_note && (
                  <div className="bg-brand-green/8 border-l-2 border-brand-green rounded-md px-2 py-1.5" data-testid={`feeling-nutri-${f.date}`}>
                    <span className="text-[9px] text-brand-green font-semibold uppercase tracking-wide">A nutricionista respondeu:</span>
                    <p className="text-xs text-brand-green mt-0.5">{f.nutri_note}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Nota da nutricionista no desafio — Item 2 Fase 4 */}
      {hasChallenge && todayFeeling?.nutri_note && (
        <div className="bg-brand-green/8 border-l-4 border-brand-green rounded-2xl px-3 py-2 mb-4" data-testid="challenge-nutri-note">
          <span className="text-[10px] text-brand-green font-semibold uppercase tracking-wide">Nota da nutricionista</span>
          <p className="text-xs text-brand-green mt-0.5">{todayFeeling.nutri_note}</p>
        </div>
      )}

      {/* Nota do dia (paciente) — Item 2 Fase 4 + P1 mood 1-5 */}
      {hasChallenge && (
        <div className="bg-white border border-brand-border rounded-2xl p-3 mb-4" data-testid="challenge-day-note">
          <label htmlFor="challenge-day-note-textarea" className="text-[10px] text-brand-muted font-medium uppercase tracking-wide block">A tua resposta de hoje</label>
          <div className="flex gap-1.5 mt-2 mb-2" data-testid="mood-scale">
            {[
              { v: 1, e: '😔', label: 'mau' },
              { v: 2, e: '😐', label: 'sem energia' },
              { v: 3, e: '🙂', label: 'ok' },
              { v: 4, e: '😊', label: 'bem' },
              { v: 5, e: '🤩', label: 'óptimo' },
            ].map(m => {
              const active = todayFeeling?.mood === m.v;
              return (
                <button
                  key={m.v}
                  type="button"
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/api/challenge-feeling`, { challenge_id: challenge.id, mood: m.v });
                      setTodayFeeling(prev => ({ ...(prev || { date: today }), mood: m.v }));
                    } catch { toast.error('Erro a guardar'); }
                  }}
                  className={`flex-1 py-1.5 rounded-xl text-xl border transition-all ${active ? 'bg-brand-green/10 border-brand-green scale-105' : 'bg-brand-bg/40 border-brand-border hover:bg-brand-bg/60'}`}
                  data-testid={`mood-${m.v}`}
                  title={m.label}
                >{m.e}</button>
              );
            })}
          </div>
          <Textarea
            id="challenge-day-note-textarea"
            name="challenge_day_note"
            value={noteDraft}
            onChange={e => { setNoteDraft(e.target.value); setNoteDirty(true); }}
            rows={2}
            maxLength={1000}
            placeholder="Algo que queiras partilhar com a tua nutricionista..."
            className="w-full mt-1 text-sm bg-brand-bg/40 border-brand-border rounded-xl px-2 py-1.5 resize-none focus:border-brand-green/40"
            data-testid="challenge-day-note-input"
            autoComplete="off"
          />
          {noteDirty && (
            <button type="button" onClick={saveNote} className="mt-1.5 text-[10px] bg-brand-green text-white rounded-full px-3 py-1" data-testid="challenge-day-note-save">Guardar nota</button>
          )}
        </div>
      )}

      {/* Daily progress + Group adherence */}
      <div className={`grid ${hasChallenge ? 'grid-cols-2' : 'grid-cols-1'} gap-3 mb-5`}>
        {total > 0 && (
          <div className="bg-white border border-brand-border rounded-2xl p-4" data-testid="daily-progress">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-brand-muted">{hasChallenge ? 'O teu progresso' : 'Progresso de hoje'}</span>
              <span className="text-sm font-heading font-semibold text-brand-text">{done}/{total}</span>
            </div>
            <div className="w-full h-2 bg-brand-border rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all duration-500 ${dayPct >= 70 ? 'bg-brand-green' : dayPct >= 40 ? 'bg-brand-gold' : 'bg-brand-muted'}`} style={{ width: `${dayPct}%` }} />
            </div>
            <FeedbackMessage pct={dayPct} />
          </div>
        )}
        {hasChallenge && (
          <div className="bg-white border border-brand-border rounded-2xl p-4" data-testid="group-progress">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-brand-muted flex items-center gap-1"><Users size={12} /> Grupo</span>
              <span className="text-sm font-heading font-semibold text-brand-text">{groupPct}%</span>
            </div>
            <div className="w-full h-2 bg-brand-border rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all ${groupPct >= 70 ? 'bg-brand-green' : groupPct >= 40 ? 'bg-brand-gold' : 'bg-brand-muted'}`} style={{ width: `${groupPct}%` }} />
            </div>
            <p className="text-[10px] text-brand-muted mt-1.5">{challengeStats?.participants || 0} participantes</p>
          </div>
        )}
      </div>

      {/* Goals checklist */}
      {displayGoals.length === 0 ? (
        <div className="text-center py-12">
          <Target size={40} className="mx-auto text-brand-placeholder mb-3" weight="duotone" />
          <p className="text-brand-muted text-sm">{hasChallenge ? 'Sem metas neste desafio.' : 'Sem metas definidas.'}</p>
          <p className="text-[10px] text-brand-placeholder mt-1">A tua nutricionista vai definir as tuas metas.</p>
        </div>
      ) : (
        <div className="space-y-2.5 mb-5">
          {displayGoals.map(goal => {
            const log = logs[goal.id];
            const status = log?.status || 'not_done';
            const Icon = STATUS_ICONS[status];
            const isQ = goal.target_type === 'quantitative';
            return (
              <div key={goal.id} className={`border rounded-2xl p-3.5 transition-all ${STATUS_BG[status]}`} data-testid={`goal-${goal.id}`}>
                <div className="flex items-start gap-3">
                  {!isQ && (
                    <button onClick={() => logGoal(goal.id, status === 'not_done' ? 'done' : status === 'done' ? 'partial' : 'not_done')} className="mt-0.5 flex-shrink-0" data-testid={`toggle-${goal.id}`}>
                      <Icon size={22} weight={status === 'not_done' ? 'regular' : 'fill'} className={STATUS_COLORS[status]} />
                    </button>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${status === 'done' ? 'text-brand-green' : 'text-brand-text'}`}>{goal.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${CAT_COLORS[goal.category] || 'bg-brand-bg text-brand-muted'}`}>{goal.category}</span>
                    </div>
                    {isQ && (
                      <div className="flex items-center gap-2 mt-2">
                        <Input type="number" value={log?.value || ''} onChange={e => handleQuantitative(goal, e.target.value)} placeholder="0" className="w-16 rounded-lg border-brand-border text-sm h-8" data-testid={`value-${goal.id}`} />
                        <span className="text-xs text-brand-muted">de {goal.target_value}</span>
                        <Icon size={16} weight={status === 'not_done' ? 'regular' : 'fill'} className={STATUS_COLORS[status]} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* TASK 1: Dias em atraso (passados, sem registo) — só aparece se existirem */}
      {(() => {
        const totalOverdue = Object.values(pendingDays).reduce((acc, arr) => acc + (arr?.length || 0), 0);
        if (totalOverdue === 0) return null;
        return (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3.5 mb-5" data-testid="overdue-section">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-heading font-medium text-sm text-amber-800 flex items-center gap-1.5">
                <Target size={14} weight="duotone" /> Dia em atraso (ontem)
                <span className="text-[10px] bg-amber-200 text-amber-900 rounded-full px-2 py-0.5 ml-1" data-testid="overdue-count">{totalOverdue}</span>
              </h3>
              <button onClick={() => setShowOverdue(v => !v)} className="text-[10px] text-amber-700 hover:underline" data-testid="overdue-toggle">{showOverdue ? 'Ocultar' : 'Mostrar'}</button>
            </div>
            <p className="text-[10px] text-amber-700 mb-3">Podes marcar metas que cumpriste ontem. Dias anteriores já não são marcáveis.</p>
            {showOverdue && (
              <div className="space-y-3">
                {goals.filter(g => (pendingDays[g.id] || []).length > 0).map(goal => (
                  <div key={goal.id} className="bg-white rounded-xl p-3 border border-amber-100" data-testid={`overdue-goal-${goal.id}`}>
                    <p className="text-xs font-medium text-brand-text mb-1.5">{goal.title}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {pendingDays[goal.id].slice(0, 14).map(d => (
                        <button
                          key={d}
                          onClick={() => logOverdueDay(goal.id, d, 'done')}
                          className="text-[10px] bg-brand-bg border border-brand-border rounded-full px-2.5 py-1 hover:bg-brand-green hover:text-white hover:border-brand-green transition-colors"
                          data-testid={`overdue-day-${goal.id}-${d}`}
                          title={`Marcar ${d} como cumprido`}
                        >
                          {new Date(d).toLocaleDateString('pt-PT', { day: 'numeric', month: 'short' })}
                        </button>
                      ))}
                      {pendingDays[goal.id].length > 14 && (
                        <span className="text-[10px] text-brand-muted self-center">+{pendingDays[goal.id].length - 14} dias mais</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Individual goals (if in challenge, show separately) */}
      {hasChallenge && individualGoals.length > 0 && (
        <div className="mb-5">
          <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-2">As tuas metas pessoais</h3>
          <div className="space-y-2">
            {individualGoals.map(goal => {
              const log = logs[goal.id];
              const status = log?.status || 'not_done';
              const Icon = STATUS_ICONS[status];
              return (
                <div key={goal.id} className={`border rounded-2xl p-3 ${STATUS_BG[status]}`}>
                  <div className="flex items-center gap-3">
                    <button onClick={() => logGoal(goal.id, status === 'not_done' ? 'done' : status === 'done' ? 'partial' : 'not_done')} className="flex-shrink-0">
                      <Icon size={20} weight={status === 'not_done' ? 'regular' : 'fill'} className={STATUS_COLORS[status]} />
                    </button>
                    <p className={`text-sm ${status === 'done' ? 'text-brand-green' : 'text-brand-text'}`}>{goal.title}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Emotional check-in */}
      {showFeeling && (
        <div className="bg-white border border-brand-border rounded-2xl p-4 mb-5 text-center" data-testid="feeling-prompt">
          <p className="text-sm text-brand-text mb-3">Como correu hoje?</p>
          <div className="flex justify-center gap-4">
            {FEELINGS.map(f => (
              <button key={f.key} onClick={() => logFeeling(f.key)} className={`flex flex-col items-center gap-1 p-3 rounded-xl border transition-all hover:scale-105 ${f.bg}`} data-testid={`feeling-${f.key}`}>
                <f.icon size={28} weight="duotone" className={f.color} />
                <span className="text-[10px] text-brand-muted">{f.key}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* "Today was hard" note */}
      <div className="mb-5">
        {!showNote ? (
          <button onClick={() => setShowNote(true)} className="w-full text-center py-2.5 text-xs text-brand-muted hover:text-brand-text transition-colors" data-testid="hard-day-btn">
            <NotePencil size={13} className="inline mr-1" /> Hoje foi difícil? Escreve aqui.
          </button>
        ) : (
          <div className="bg-white border border-brand-border rounded-2xl p-4">
            <p className="text-xs text-brand-muted mb-2">Partilha o que sentiste (fica entre ti e a tua nutricionista)</p>
            <Textarea value={dailyNote} onChange={e => setDailyNote(e.target.value)} placeholder="O que aconteceu hoje..." className="rounded-xl border-brand-border text-sm resize-none" rows={2} data-testid="daily-note" />
            <div className="flex gap-2 mt-2">
              <button onClick={async () => { if (dailyNote.trim()) { await axios.post(`${API}/api/goal-logs`, { goal_id: 'daily_note', date: today, status: 'done', note: dailyNote }); toast.success('Nota guardada'); } setShowNote(false); setDailyNote(''); }} className="bg-brand-green text-white rounded-full px-4 py-1.5 text-xs font-medium">Guardar</button>
              <button onClick={() => { setShowNote(false); setDailyNote(''); }} className="text-xs text-brand-muted">Cancelar</button>
            </div>
          </div>
        )}
      </div>

      {/* Weekly summary */}
      {Object.keys(weekly).length > 0 && (
        <div className="bg-white border border-brand-border rounded-2xl p-4" data-testid="weekly-summary">
          <div className="flex items-center gap-1.5 mb-3">
            <TrendUp size={14} className="text-brand-green" />
            <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider">Esta semana</h3>
          </div>
          <div className="space-y-2">
            {Object.values(weekly).map((w, i) => (
              <div key={i} className="flex items-center gap-3">
                <p className="text-xs text-brand-text break-words flex-1 min-w-0">{w.title}</p>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="w-14 h-1.5 bg-brand-border rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${w.pct >= 70 ? 'bg-brand-green' : w.pct >= 40 ? 'bg-brand-gold' : 'bg-brand-muted'}`} style={{ width: `${w.pct}%` }} />
                  </div>
                  <span className={`text-[10px] font-medium w-7 text-right ${w.pct >= 70 ? 'text-brand-green' : 'text-brand-muted'}`}>{w.pct}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Public challenges catalogue — browse & self-join */}
      {publicChallenges.length > 0 && (
        <div className="mt-6" data-testid="public-challenges-section">
          <div className="flex items-center gap-1.5 mb-3">
            <Trophy size={16} weight="duotone" className="text-brand-gold" />
            <h3 className="font-heading font-medium text-sm text-brand-text">Desafios para participar</h3>
          </div>
          <div className="space-y-3">
            {publicChallenges.map(c => (
              <div key={c.id} className="bg-white border border-brand-border rounded-2xl p-4" data-testid={`public-challenge-${c.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <h4 className="font-heading font-medium text-brand-text break-words">{c.title}</h4>
                    {c.description && <Md as="p" className="text-xs text-brand-muted mt-1 break-words whitespace-pre-wrap">{c.description}</Md>}
                    {c.target_audience && (
                      <span className="inline-block mt-2 text-[10px] font-medium px-2 py-0.5 rounded-full bg-brand-gold/15 text-brand-gold-hover" data-testid={`public-audience-${c.id}`}>{c.target_audience}</span>
                    )}
                    <div className="flex items-center gap-3 mt-2 text-[10px] text-brand-placeholder flex-wrap">
                      {c.start_date && <span className="flex items-center gap-1"><CalendarBlank size={12} /> Início {fmtDatePt(c.start_date)}</span>}
                      {c.remaining_days != null && <span data-testid={`public-remaining-${c.id}`}>{c.remaining_days > 0 ? `${c.remaining_days} dia(s) restantes` : 'A terminar'}</span>}
                      {c.participants_count > 0 && <span className="flex items-center gap-1"><Users size={12} /> {c.participants_count}</span>}
                    </div>
                  </div>
                  {c.already_joined ? (
                    <span className="flex-shrink-0 text-[11px] font-medium text-brand-green flex items-center gap-1 px-3 py-2" data-testid={`joined-badge-${c.id}`}><CheckCircle size={15} weight="fill" /> Inscrita</span>
                  ) : (
                    <button onClick={() => joinChallenge(c)} disabled={joiningId === c.id} className="flex-shrink-0 bg-brand-green text-white rounded-full px-4 py-2 text-xs font-medium disabled:opacity-50" data-testid={`join-challenge-${c.id}`}>
                      {joiningId === c.id ? 'A inscrever...' : 'Juntar-me'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
